import React from "react";
import { Box, Typography, Chip } from "@mui/material";
import { useIntl } from "react-intl";
import { CheckCircle } from "lucide-react";
import ProfilePicture from "bundles/common/components/ProfilePicture";
import HTML from "bundles/common/components/HTML";
import { formatJobOfferLocations } from "bundles/common/utils/utils";

const NewJobOfferCard = ({ offer, onClick, hideDescription = false, applied = false }) => {
  const intl = useIntl();

  const handleCardClick = () => {
    if (onClick) {
      onClick(offer);
    }
  };

  const getCategoryLabels = () => {
    if (!Array.isArray(offer?.categories) || offer.categories.length === 0) return [];
    return offer.categories.map((slug) => {
      const fallback = slug
        .replace(/-/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
      return intl.formatMessage({ id: `categories.${slug}`, defaultMessage: fallback });
    });
  };

  const categoryLabels = getCategoryLabels();

  // Translate the first job status slug
  const getJobStatus = () => {
    const status = Array.isArray(offer?.job_statuses) && offer.job_statuses.length > 0
      ? offer.job_statuses[0]
      : offer?.job_status;
    if (!status) return null;
    return intl.formatMessage(
      { id: `candidate.status_map.${status}`, defaultMessage: status },
    );
  };

  // Translate the offer language
  const getLanguage = () => {
    const lang = offer?.offer_language;
    if (!lang) return null;
    return intl.formatMessage(
      { id: `languages.${lang}`, defaultMessage: lang },
    );
  };

  const descriptionHtml = offer?.description || offer?.job_description;
  const hasDescription =
    descriptionHtml &&
    String(descriptionHtml)
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim().length > 0;

  /** Two lines at 14px / line-height 1.43 — fixed slot so short summaries match tall ones in a row */
  const summaryLineHeight = 1.43;
  const summaryLines = 2;
  const summarySlotHeight = `calc(${summaryLines * summaryLineHeight}em + 2px)`;

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        width: "100%",
        maxWidth: "585px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        borderRadius: "10px",
        padding: "20px",
        backgroundColor: "white",
        border: "2px solid hsl(160 10% 90%)",
        cursor: "pointer",
        boxShadow: "0 1px 3px rgba(2, 51, 49, 0.06)",
        "&:hover": {
          borderColor: "hsla(145, 63%, 42%, 0.3)",
          boxShadow: "0 10px 15px -3px hsla(145, 63%, 42%, 0.05), 0 4px 6px -4px hsla(145, 63%, 42%, 0.05)",
        },
        "&:focus-visible": {
          outline: "none",
          borderColor: "hsl(153 68% 25%)",
          boxShadow: "0 0 0 3px rgba(22, 109, 76, 0.22)",
        },
        transition: "all 0.3s ease",
      }}
    >
      {/* Top section with logo and job info */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "366px",
          height: "64px",
          display: "flex",
          alignItems: "center",
          mb: 2,
          flexShrink: 0,
        }}
      >
        {/* Company Logo */}
        <Box sx={{ mr: 3, flexShrink: 0 }}>
          <ProfilePicture
            picture={offer?.company?.logo_url}
            picturesmall={offer?.company?.small_logo_url}
            size={60}
            initials={offer?.company?.name?.[0]}
            alt={`Logo ${offer?.company?.name}`}
          />
        </Box>

        {/* Job Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Job Title */}
          <Typography
            sx={{
              fontFamily: "Segoe UI Semibold, sans-serif",
              fontSize: "20px",
              lineHeight: 1.334,
              color: "#023331",
              mb: 0.5,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {offer?.job_title}
          </Typography>

          {/* Company Name */}
          <Typography
            sx={{
              fontFamily: "Segoe UI Regular, sans-serif",
              fontSize: "14px",
              color: "#023331",
              mb: 0.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              letterSpacing: "-0.3333px",
            }}
          >
            {offer?.company?.name}
          </Typography>

          {/* Location */}
          <Typography
            sx={{
              fontFamily: "Segoe UI Semibold, sans-serif",
              fontSize: "14px",
              color: "#023331",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              letterSpacing: "-0.3333px",
            }}
          >
            {formatJobOfferLocations(offer?.locations || []).join(" - ")}
          </Typography>
        </Box>
      </Box>

      {/* Pill chips — only rendered when data exists */}
      <Box sx={{ display: "flex", gap: 1.25, mb: !hideDescription ? 2.5 : 0, flexWrap: "wrap", flexShrink: 0 }}>
        {applied && (
          <Chip
            icon={<CheckCircle size={13} color="#166d4c" />}
            label={intl.formatMessage({ id: "candidate.job_detail.applied_badge", defaultMessage: "Postulé" })}
            size="small"
            sx={{
              fontFamily: "Segoe UI Semibold, sans-serif",
              fontSize: "14px",
              height: "24px",
              borderRadius: "20px",
              backgroundColor: "rgba(22, 109, 76, 0.1)",
              color: "#166d4c",
              px: 1,
              "& .MuiChip-label": { fontFamily: "Segoe UI Semibold, sans-serif", paddingLeft: "4px", paddingRight: 0 },
            }}
          />
        )}
        {categoryLabels.map((label, index) => (
          <Chip
            key={`category-${index}-${label}`}
            label={label}
            size="small"
            sx={{
              fontFamily: "Segoe UI Regular, sans-serif",
              fontSize: "14px",
              height: "24px",
              borderRadius: "20px",
              backgroundColor: "#023331",
              color: "white",
              px: 1.5,
              "& .MuiChip-label": { fontFamily: "Segoe UI Regular, sans-serif", padding: 0 },
            }}
          />
        ))}
        {getJobStatus() && (
          <Chip
            label={getJobStatus()}
            size="small"
            sx={{
              fontFamily: "Segoe UI Regular, sans-serif",
              fontSize: "14px",
              height: "24px",
              borderRadius: "20px",
              backgroundColor: "#166d4c",
              color: "white",
              px: 1.5,
              "& .MuiChip-label": { fontFamily: "Segoe UI Regular, sans-serif", padding: 0 },
            }}
          />
        )}
        {getLanguage() && (
          <Chip
            label={getLanguage()}
            size="small"
            sx={{
              fontFamily: "Segoe UI Regular, sans-serif",
              fontSize: "14px",
              height: "24px",
              borderRadius: "20px",
              backgroundColor: "white",
              color: "#023331",
              border: "1px solid #166d4c",
              px: 1.5,
              "& .MuiChip-label": { fontFamily: "Segoe UI Regular, sans-serif", padding: 0 },
            }}
          />
        )}
      </Box>

      {/* Fills extra row height so summaries align at the bottom when cards stretch */}
      <Box sx={{ flex: 1, minHeight: 0, flexShrink: 0 }} aria-hidden />

      {/* Job summary: always reserve exactly two lines (same height whether text is short or long) */}
      {!hideDescription ? (
        <Box
          sx={{
            fontFamily: "Segoe UI Regular, sans-serif",
            fontSize: "14px",
            lineHeight: summaryLineHeight,
            color: "#1b1b1b",
            letterSpacing: "-0.3333px",
            height: summarySlotHeight,
            minHeight: summarySlotHeight,
            maxHeight: summarySlotHeight,
            overflow: "hidden",
            wordBreak: "break-word",
            flexShrink: 0,
            "& p": { margin: 0, lineHeight: "inherit" },
            "& ul, & ol": { margin: "0 0 0.35em 0", paddingLeft: "1.25rem" },
            "& *": { lineHeight: "inherit" },
          }}
        >
          {hasDescription ? (
            <HTML
              html={descriptionHtml}
              style={{
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: summaryLines,
                lineClamp: summaryLines,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            />
          ) : null}
        </Box>
      ) : null }
    </Box>
  );
};

export default NewJobOfferCard;
